import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { supabase } from '../lib/supabase';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store actingAs child ID for parent impersonation
let actingAsChildId: string | null = null;

export function setActingAsChild(childId: string | null) {
  actingAsChildId = childId;
}

// Request interceptor: attach Supabase access token to every request
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  if (actingAsChildId) {
    config.headers['X-Acting-As'] = actingAsChildId;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Client-side GET cache — avoids redundant network round-trips
// ---------------------------------------------------------------------------

interface CacheEntry {
  data: any;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();

const DEFAULT_TTL = 30_000;       // 30 seconds for most data
const STATIC_TTL = 120_000;       // 2 minutes for rarely-changing data

// Patterns that change infrequently and can be cached longer
const longCachePatterns = [
  /^\/fields/,
  /^\/clubs/,
  /^\/leagues$/,
  /^\/leagues\?/,
];

function getCacheKey(url: string, params?: Record<string, any>): string {
  const paramStr = params ? '?' + new URLSearchParams(params as any).toString() : '';
  return `${url}${paramStr}`;
}

function getTTL(url: string): number {
  return longCachePatterns.some((p) => p.test(url)) ? STATIC_TTL : DEFAULT_TTL;
}

/** Invalidate cache entries whose keys match any of the given prefixes. */
function invalidateCache(...prefixes: string[]) {
  for (const key of Array.from(cache.keys())) {
    if (prefixes.some((p) => key.startsWith(p))) {
      cache.delete(key);
    }
  }
}

/** Clear the entire cache (e.g., on logout). */
export function clearApiCache() {
  cache.clear();
}

// Wrap the raw axios methods to add caching on GET and auto-invalidation on mutations.

const rawGet = api.get.bind(api);
const rawPost = api.post.bind(api);
const rawPut = api.put.bind(api);
const rawPatch = api.patch.bind(api);
const rawDelete = api.delete.bind(api);

api.get = (<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  const key = getCacheKey(url, config?.params);
  const entry = cache.get(key);
  const ttl = getTTL(url);

  if (entry && Date.now() - entry.timestamp < ttl) {
    // Return cached response (wrapped in a resolved promise shaped like AxiosResponse)
    return Promise.resolve({
      data: entry.data,
      status: 200,
      statusText: 'OK (cached)',
      headers: {},
      config: config || {},
    } as AxiosResponse<T>);
  }

  return rawGet<T>(url, config).then((response) => {
    cache.set(key, { data: response.data, timestamp: Date.now() });
    return response;
  });
}) as typeof api.get;

// Mutations: invalidate related cache entries after success
function extractResource(url: string): string {
  // "/teams/123/members" → "/teams"
  const match = url.match(/^(\/[a-z-]+)/);
  return match ? match[1] : url;
}

api.post = (<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return rawPost<T>(url, data, config).then((response) => {
    invalidateCache(extractResource(url));
    return response;
  });
}) as typeof api.post;

api.put = (<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return rawPut<T>(url, data, config).then((response) => {
    invalidateCache(extractResource(url));
    return response;
  });
}) as typeof api.put;

api.patch = (<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return rawPatch<T>(url, data, config).then((response) => {
    invalidateCache(extractResource(url));
    return response;
  });
}) as typeof api.patch;

api.delete = (<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
  return rawDelete<T>(url, config).then((response) => {
    invalidateCache(extractResource(url));
    return response;
  });
}) as typeof api.delete;

export default api;
